import type { ReactNode } from 'react'
import { Link, NavLink } from 'react-router'
import { useMe } from '../api/hooks.ts'
import { Avatar } from './Avatar.tsx'
import { Logo } from './EmptyState.tsx'
import {
  IconChart,
  IconCheck,
  IconClock,
  IconEye,
  IconHome,
  IconPlus,
  IconRepeat,
  IconSettings,
  IconShield,
  IconSparkle,
  IconTabMine,
  IconTabOutbox,
  IconTabTeam,
  IconTag,
} from './icons.tsx'

// Боковое меню CRM на широком экране (компьютер): все разделы сразу, без «Ещё».

interface Item {
  to: string
  label: string
  icon: ReactNode
  badge?: number
  end?: boolean
}

function Group({ title, items }: { title?: string; items: Item[] }) {
  return (
    <div className="sidebar__group">
      {title && <div className="sidebar__title">{title}</div>}
      {items.map((i) => (
        <NavLink
          key={i.to}
          to={i.to}
          end={i.end}
          className={({ isActive }) => `sidebar__item${isActive ? ' sidebar__item--active' : ''}`}
        >
          <span className="sidebar__icon">{i.icon}</span>
          <span className="grow">{i.label}</span>
          {i.badge ? <span className="sidebar__badge">{i.badge > 99 ? '99+' : i.badge}</span> : null}
        </NavLink>
      ))}
    </div>
  )
}

export function Sidebar({ mineBadge, outboxBadge }: { mineBadge: number; outboxBadge: number }) {
  const me = useMe()
  const isManager = Boolean(me.data?.isManager)
  return (
    <aside className="sidebar" aria-label="Разделы">
      <Link to="/home" className="sidebar__brand">
        <Logo size={30} />
        <span>OVO CRM</span>
      </Link>
      <Link to="/tasks/new" className="btn btn--primary btn--block sidebar__new">
        <IconPlus size={18} strokeWidth={2.2} /> Поставить задачу
      </Link>
      <nav className="sidebar__nav">
        <Group
          items={[
            { to: '/home', label: 'Главная', icon: <IconHome size={20} /> },
            { to: '/', label: 'Мои задачи', icon: <IconTabMine size={20} />, badge: mineBadge, end: true },
            { to: '/outbox', label: 'Поставил', icon: <IconTabOutbox size={20} />, badge: outboxBadge },
            { to: '/watching', label: 'Наблюдаю', icon: <IconEye size={20} /> },
            { to: '/recurring', label: 'Регулярные задачи', icon: <IconRepeat size={20} /> },
          ]}
        />
        <Group
          title="Команда"
          items={[
            ...(isManager ? [{ to: '/team', label: 'Моя команда', icon: <IconTabTeam size={20} /> }] : []),
            { to: '/reports', label: 'Отчёт за неделю', icon: <IconChart size={20} /> },
            { to: '/shifts', label: 'Рабочее время', icon: <IconClock size={20} /> },
          ]}
        />
        <Group
          title="Процессы"
          items={[
            { to: '/ideas', label: 'Ящик идей', icon: <IconSparkle size={20} /> },
            { to: '/approvals', label: 'Заявки', icon: <IconCheck size={20} /> },
            { to: '/projects', label: 'Проекты и клиенты', icon: <IconTag size={20} /> },
          ]}
        />
        <Group
          items={[
            { to: '/settings', label: 'Настройки', icon: <IconSettings size={20} /> },
            ...(me.data?.isAdmin ? [{ to: '/admin', label: 'Админка', icon: <IconShield size={20} /> }] : []),
          ]}
        />
      </nav>
      {me.data && (
        <Link to="/home" className="sidebar__me">
          <Avatar name={me.data.displayName} id={me.data.id} size={36} photo={me.data.photoVersion} />
          <span className="grow">
            <span className="sidebar__me-name">{me.data.displayName}</span>
            <span className="hint">{me.data.position || 'Сотрудник OVO'}</span>
          </span>
        </Link>
      )}
    </aside>
  )
}
